import React, { useState, useEffect } from "react";
import { Box, Menu, MenuItem, Typography, ClickAwayListener } from "@mui/material";
import { Search, KeyboardArrowDown, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const FloatingSearchBar = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("Women");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Show after a slight delay
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = () => {
    if (search.trim().length > 0) {
      navigate("/search", { state: { search: search.trim(), gender } });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <ClickAwayListener onClickAway={() => {
          if (isExpanded && !anchorEl && !search) {
            setIsExpanded(false);
          }
        }}>
          <Box
            component={motion.div}
            layout
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            onClick={() => {
              if (!isExpanded) setIsExpanded(true);
            }}
            sx={{
              position: "fixed",
              zIndex: 1000,
              boxSizing: "border-box",
              overflow: "hidden", // Prevents children from spilling out during animation

              // Dynamic Layout Styles
              ...(isExpanded ? {
                bottom: { xs: 16, sm: 24, md: 16 },
                left: 0,
                right: 0,
                margin: "0 auto",
                width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
                maxWidth: "none",
                backgroundColor: "#1A1A1A",
                borderRadius: "8px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                padding: 0,
                border: "none",
                cursor: "default",
              } : {
                bottom: { xs: 16, md: 24 },
                right: { xs: 16, md: 24 },
                left: "auto",
                margin: 0,
                width: "auto",
                maxWidth: "none",
                backgroundColor: "#222",
                borderRadius: "100px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: { xs: "4px 12px 4px 4px", md: "8px 20px 8px 8px" },
                border: "1px solid rgba(255,107,0,0.4)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4), 0 0 10px rgba(255,107,0,0.15)",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 6px 24px rgba(0,0,0,0.5), 0 0 15px rgba(255,107,0,0.3)",
                }
              }),
            }}
          >
            {isExpanded ? (
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "none",
                  boxSizing: "border-box",
                  padding: { xs: "14px 12px", sm: "10px 16px", md: "14px 16px" },
                }}
              >
                {/* Left Search Icon */}
                <Search sx={{ display: { xs: "none", sm: "block" }, color: "#aaa", mr: { xs: 0.5, sm: 1 }, ml: { xs: 0, sm: 1 }, fontSize: { xs: "20px", sm: "24px" } }} />

                {/* Input Container with Custom Placeholder */}
                <Box sx={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", marginRight: { xs: "8px", sm: "16px" } }}>
                  {!search && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        pointerEvents: "none",
                        overflow: "hidden",
                        containerType: "inline-size",
                      }}
                    >
                      <Box
                        sx={{
                          whiteSpace: "nowrap",
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: { xs: "13px", sm: "16px" },
                          fontFamily: "Poppins-Regular",
                          display: "inline-block",
                          transform: "translateX(10px)",
                          "@keyframes marquee": {
                            "0%": { transform: "translateX(10px)" },
                            "50%": { transform: "translateX(calc(100cqw - 100% - 15px))" },
                            "100%": { transform: "translateX(10px)" },
                          },
                          animation: { xs: "marquee 11s linear 0.5s infinite", sm: "none" }
                        }}
                      >
                        Search styles, outfits, trends across all brands...
                      </Box>
                    </Box>
                  )}

                  <Box
                    component="input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder=""
                    sx={{
                      width: "100%",
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: { xs: "14px", sm: "18px" },
                      fontFamily: "Poppins-Regular",
                      color: "#fff",
                      height: "40px",
                    }}
                  />
                </Box>

                {/* Gender Selector */}
                <Box
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "4px 10px",
                    marginRight: { xs: "6px", sm: "16px" },
                    border: "1px solid rgba(255, 255, 255, 0.4)",
                    borderRadius: "20px",
                    transition: "all 0.2s",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <Typography sx={{ fontFamily: "Poppins-Medium", fontSize: { xs: "11px", sm: "15px" }, color: "#fff", textTransform: "uppercase" }}>
                    {gender}
                  </Typography>
                  <KeyboardArrowDown sx={{ fontSize: { xs: "16px", sm: "22px" }, ml: 0.2, color: "#fff" }} />
                </Box>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  disableScrollLock={true}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
                      minWidth: "120px",
                      mb: 1,
                    }
                  }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {["Women", "Men", "Kids"].map((g) => (
                    <MenuItem
                      key={g}
                      onClick={() => {
                        setGender(g);
                        setAnchorEl(null);
                      }}
                      selected={g === gender}
                      sx={{ fontFamily: "Poppins-Medium", fontSize: "13px" }}
                    >
                      {g}
                    </MenuItem>
                  ))}
                </Menu>

                <Box
                  onClick={handleSearch}
                  sx={{
                    order: { xs: -1, sm: 0 },
                    marginRight: { xs: "12px", sm: 0 },
                    backgroundColor: "#FF9B80",
                    borderRadius: "12px",
                    minWidth: { xs: "36px", sm: "52px", md: "100px" },
                    width: { xs: "36px", sm: "52px", md: "100px" },
                    height: { xs: "36px", sm: "52px", md: "40px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "background-color 0.2s",
                    "&:hover": { backgroundColor: "#ff8a6a" },
                  }}
                >
                  <Search sx={{ color: "#fff", fontSize: { xs: "20px", sm: "26px" } }} />
                </Box>

                {/* Close Button Panel */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearch("");
                    setIsExpanded(false);
                  }}
                  sx={{
                    width: { xs: "48px", sm: "56px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderLeft: "1px solid rgba(255,255,255,0.1)",
                    marginLeft: { xs: "8px", sm: "16px" },
                    marginRight: { xs: "-12px", sm: "-16px" }, // Pull it to the edge
                    alignSelf: "stretch", // Fill height
                    backgroundColor: "transparent",
                    transition: "background-color 0.2s",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }
                  }}
                >
                  <Close sx={{ color: "#aaa" }} />
                </Box>
              </Box>
            ) : (
              // --- COMPACT FAB (Screenshot styling) ---
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                transition={{ duration: 0.2, delay: 0.1 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.75, md: 1.25 },
                  width: "100%",
                }}
              >
                <Box
                  component={motion.div}
                  layout="position"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#3A2A22",
                    borderRadius: "50%",
                    p: { xs: 0.5, md: 0.8 },
                    flexShrink: 0,
                  }}
                >
                  <Search sx={{ fontSize: { xs: "16px", md: "20px" }, color: "#FF6B00" }} />
                </Box>
                <Box component={motion.div} layout="position" sx={{ flex: 1, whiteSpace: "nowrap", pr: 0.5 }}>
                  <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "12px", md: "14px" }, color: "#fff", lineHeight: 1.2 }}>
                    Global AI Search
                  </Typography>
                  <Typography sx={{ fontFamily: "Poppins-Medium", fontSize: { xs: "6px", md: "8px" }, color: "#aaa", letterSpacing: "0.5px", textTransform: "uppercase", mt: 0.3 }}>
                    SEARCH ACROSS ALL BRANDS
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      )}
    </AnimatePresence>
  );
};

export default FloatingSearchBar;
